import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('fex_token'));
  const [loading, setLoading] = useState(true);
  const [cashSession, setCashSession] = useState(null);
  const [checkingSession, setCheckingSession] = useState(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Check for open cash session
      await checkCashSession();
    } catch (error) {
      console.error('Auth error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const checkCashSession = useCallback(async () => {
    if (!token) return null;
    setCheckingSession(true);
    try {
      const response = await axios.get(`${API}/cash-session/current`);
      setCashSession(response.data);
      return response.data;
    } catch (error) {
      console.error('Cash session error:', error);
      setCashSession({ has_open_session: false, session: null });
      return null;
    } finally {
      setCheckingSession(false);
    }
  }, [token]);

  const openCashSession = async (initialCash, notes = null) => {
    const response = await axios.post(`${API}/cash-session/open`, {
      initial_cash: initialCash,
      notes
    });
    await checkCashSession();
    return response.data;
  };

  const closeCashSession = async (countedCash, notes = null) => {
    const response = await axios.post(`${API}/cash-session/close`, {
      counted_cash: countedCash,
      notes
    });
    await checkCashSession();
    return response.data;
  };

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, { username, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('fex_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    
    // Check for open cash session after login
    setTimeout(() => checkCashSession(), 100);
    
    return userData;
  };

  const register = async (email, password, name, role = 'vendedor') => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('fex_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setCashSession(null);
  };

  const isAdmin = () => user?.role === 'admin';
  
  const hasOpenCashSession = () => cashSession?.has_open_session === true;

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      isAdmin,
      cashSession,
      checkingSession,
      checkCashSession,
      openCashSession,
      closeCashSession,
      hasOpenCashSession
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
